import { AppState } from '@/model/store';
import { calculateTotalExistingAssetsValue } from './fundingSufficiency';

/**
 * Helper function to check if a policy was added via shop insurance.
 * Only returns true for active (non-deactivated) policies.
 */
export function isPolicyAddedViaShopInsurance(policy: any): boolean {
  return (
    (policy?.addedViaShopInsurance === true || policy?.addedViaShopInsurance === 'true') &&
    !policy?.isDeactivated
  );
}

/**
 * Calculates the total premium cost for all policies added via shop insurance.
 * Uses the pre-calculated policyTotalCost from each PolicyFundingSource.
 */
export function calculateTotalPoliciesCost(
  ltcPolicies: Record<string, PolicyFundingSource>,
): number {
  return Object.values(ltcPolicies).reduce((total, policy) => {
    if (!isPolicyAddedViaShopInsurance(policy)) return total;
    return total + (policy.policyTotalCost || 0);
  }, 0);
}

/**
 * Calculates the funding gap between total policy costs and allocated funds.
 * Returns positive number if there's a gap (costs > allocations),
 * zero if fully funded, or negative if over-funded.
 */
export function calculatePolicyFundingGap(fullState?: AppState): number {
  if (!fullState) return 0;

  const multipleFundingSources = fullState?.client?.data?.multipleFundingSources;
  if (!multipleFundingSources) return 0;

  const ltcPolicies = multipleFundingSources.ltcPolicy || {};
  const policiesAddedViaShop = Object.values(ltcPolicies).filter(policy =>
    isPolicyAddedViaShopInsurance(policy),
  );

  if (policiesAddedViaShop.length === 0) return 0;

  const totalPolicyCost = calculateTotalPoliciesCost(ltcPolicies);
  const totalExistingAssetsValue = calculateTotalExistingAssetsValue(multipleFundingSources);

  return totalPolicyCost - totalExistingAssetsValue;
}

/**
 * Gets all insurance quote IDs for policies added via shop insurance.
 * Returns a Set of quote IDs for efficient lookup.
 */
export function getShopInsuranceQuoteIds(
  multipleFundingSources?: MultipleFundingSources,
): Set<string> {
  const quoteIds = new Set<string>();
  const ltcPolicies = multipleFundingSources?.ltcPolicy || {};

  Object.values(ltcPolicies).forEach(policy => {
    if (isPolicyAddedViaShopInsurance(policy) && policy.insuranceQuoteId) {
      quoteIds.add(policy.insuranceQuoteId);
    }
  });

  return quoteIds;
}
